import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PageHeader, EmptyState, Field } from "../components/Shared";
import { ProofImage } from "../components/ProofImage";
import { ImageUpload } from "../components/ImageUpload";
import { formatDateID } from "../components/DateField";
import { Scale, Loader2, Pencil, CalendarClock, FileDown, Trash2 } from "lucide-react";

const STATUS_STYLE = {
  FILLED: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  DUE_TODAY: "border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF8A80]",
  OVERDUE: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  UPCOMING: "border-white/10 text-slate-400",
  PENDING: "border-white/10 text-slate-400",
};

export default function Measurements() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const isMember = user?.role === "MEMBER";
  const [ctx, setCtx] = useState(null);
  const [batchId, setBatchId] = useState("");
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState("");
  const [sort, setSort] = useState("behind");
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [reporting, setReporting] = useState(false);

  const deletePhoto = async (measurementId, slot) => {
    try {
      await api.delete(`/measurements/${measurementId}/photo/${slot}`);
      toast.success(t("measure.photoDeleted"));
      loadDetail();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const deleteData = async (measurementId) => {
    if (!window.confirm(t("measure.confirmDelete"))) return;
    try {
      await api.delete(`/measurements/${measurementId}`);
      toast.success(t("measure.dataDeleted"));
      loadDetail(); loadBatch();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const toggleGallery = async (measurementId, enabled) => {
    try {
      await api.post(`/measurements/${measurementId}/gallery`, { enabled });
      toast.success(enabled ? t("measure.galleryOn") : t("measure.galleryOff"));
      loadDetail();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const downloadReport = async () => {
    setReporting(true);
    try {
      const res = await api.get(`/reports/member/${selected}/progress.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Rapor_${(detail?.full_name || selected).replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(extractError(e).message); }
    finally { setReporting(false); }
  };

  useEffect(() => {
    api.get("/measurements/context").then(({ data }) => {
      setCtx(data.data);
      if (!isMember && data.data.batches?.length) setBatchId(data.data.batches[0].batch_id);
    }).catch((e) => toast.error(extractError(e).message));
  }, [isMember]);

  const loadBatch = useCallback(async () => {
    if (isMember || !batchId) return;
    try {
      const { data } = await api.get(`/measurements/batch/${batchId}?sort=${sort}`);
      setMembers(data.data.members);
      setSelected((prev) => prev || data.data.members[0]?.user_id || "");
    } catch (e) { toast.error(extractError(e).message); }
  }, [isMember, batchId, sort]);

  const loadDetail = useCallback(async () => {
    try {
      const { data } = isMember ? await api.get("/measurements/me") : (selected ? await api.get(`/measurements/member/${selected}`) : { data: { data: null } });
      setDetail(data.data);
    } catch (e) { toast.error(extractError(e).message); }
  }, [isMember, selected]);

  useEffect(() => { loadBatch(); }, [loadBatch]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const canRecord = ctx?.can_record;
  const chartData = (detail?.checkpoints || []).filter((c) => c.measurement).map((c) => ({
    name: c.label,
    weight: c.measurement.weight,
    fat: c.measurement.body_fat,
  }));

  return (
    <div data-testid="measurements-page" className="space-y-6">
      <PageHeader eyebrow={t("measure.eyebrow")} title={t("measure.title")}
        subtitle={isMember ? t("measure.subtitleMember") : t("measure.subtitleCoach")} />

      <div className="rounded-xl border border-white/10 bg-[#10121B] px-4 py-3 text-xs text-slate-400 inline-flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-[#10B981]" /> {t("measure.reminderHint")}
      </div>

      {!isMember && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <label className="text-slate-300 text-sm">{t("measure.batch")}</label>
            <select data-testid="measure-batch-select" value={batchId} onChange={(e) => { setBatchId(e.target.value); setSelected(""); }}
              className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
              {(ctx?.batches || []).map((b) => <option key={b.batch_id} value={b.batch_id}>{b.code} — {b.name}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-slate-300 text-sm">{t("measure.sortBy")}</label>
            <select data-testid="measure-sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
              className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
              {["behind", "filled", "name"].map((s) => <option key={s} value={s}>{t(`measure.sort_${s}`)}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-slate-300 text-sm">{t("measure.member")}</label>
            <select data-testid="measure-member-select" value={selected} onChange={(e) => setSelected(e.target.value)}
              className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
              <option value="">{t("measure.pickMember")}</option>
              {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.full_name || m.participant_code} · {m.filled}/4{m.overdue ? ` · ${m.overdue}⚠` : ""}</option>)}
            </select>
          </div>
        </div>
      )}

      {isMember && <p className="text-xs text-slate-500">{t("measure.readOnly")}</p>}

      {!isMember && selected && (
        <Button data-testid="measure-report-btn" onClick={downloadReport} disabled={reporting}
          className="bg-[#10B981] hover:bg-emerald-500 text-white font-semibold h-10">
          {reporting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t("common.loading")}</> : <><FileDown className="h-4 w-4 mr-1" /> {t("reportPdf")}</>}
        </Button>
      )}

      {!detail ? <EmptyState testId="measure-empty" text={t("measure.noData")} /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {detail.checkpoints.map((c) => (
              <Card key={c.key} data-testid={`measure-card-${c.key}`} className="bg-[#161923] border-white/5">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <div className="font-[Outfit] font-extrabold text-white flex items-center gap-2">
                        {c.label}
                        {c.is_baseline && <span className="rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30 px-2 py-0.5 text-[10px] uppercase tracking-wider">{t("measure.baseline")}</span>}
                        {c.is_final && <span className="rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30 px-2 py-0.5 text-[10px] uppercase tracking-wider">{t("measure.final")}</span>}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {t("measure.due")}: {c.due_date ? formatDateID(c.due_date, lang) : t("measure.noSchedule")}
                      </div>
                    </div>
                    <span data-testid={`measure-status-${c.key}`}
                      className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLE[c.status]}`}>
                      {t(`measure.status_${c.status}`)}
                    </span>
                  </div>
                  {c.measurement ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <Stat label={t("measure.weight")} value={c.measurement.weight} unit="kg" testId={`measure-weight-${c.key}`} />
                        <Stat label={t("measure.bodyFat")} value={c.measurement.body_fat} unit="%" testId={`measure-fat-${c.key}`} />
                      </div>
                      {!c.is_baseline && (c.measurement.weight_delta !== null || c.measurement.body_fat_delta !== null) && (
                        <div data-testid={`measure-delta-${c.key}`} className="text-xs text-slate-400">
                          {t("measure.deltaFromStart")}:{" "}
                          {c.measurement.weight_delta !== null && <span className={c.measurement.weight_delta <= 0 ? "text-emerald-300" : "text-amber-300"}>{c.measurement.weight_delta > 0 ? "+" : ""}{c.measurement.weight_delta} kg</span>}
                          {c.measurement.body_fat_delta !== null && <span className={`ml-2 ${c.measurement.body_fat_delta <= 0 ? "text-emerald-300" : "text-amber-300"}`}>{c.measurement.body_fat_delta > 0 ? "+" : ""}{c.measurement.body_fat_delta}%</span>}
                        </div>
                      )}
                      {c.photo_slot && (() => {
                        const fid = c.photo_slot === "before" ? c.measurement.photo_before_file_id : c.measurement.photo_after_file_id;
                        if (!fid) return null;
                        return (
                          <div className="space-y-1">
                            <div className="text-[11px] text-slate-500">{t(`measure.photo_${c.photo_slot}`)}</div>
                            <ProofImage fileId={fid} testId={`measure-photo-${c.photo_slot}-${c.key}`} className="h-24 w-24" />
                            {canRecord && (
                              <Button data-testid={`measure-photo-delete-${c.photo_slot}-${c.key}`} variant="ghost"
                                onClick={() => deletePhoto(c.measurement.measurement_id, c.photo_slot)}
                                className="h-7 px-2 text-[11px] text-[#FF8A80] hover:bg-[#FF3B30]/10">
                                <Trash2 className="h-3.5 w-3.5 mr-1" /> {t("measure.deletePhoto")}
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                      {c.is_final && canRecord && (
                        <label className="flex items-start gap-2 text-xs text-slate-300">
                          <input type="checkbox" data-testid={`measure-gallery-${c.key}`} className="mt-0.5 accent-[#10B981]"
                            checked={!!c.measurement.public_gallery}
                            onChange={(e) => toggleGallery(c.measurement.measurement_id, e.target.checked)} />
                          <span>{t("measure.galleryConsent")}</span>
                        </label>
                      )}
                      {c.measurement.note && <p className="text-xs text-slate-400">{c.measurement.note}</p>}
                    </div>
                  ) : <p className="text-sm text-slate-500">{t("measure.noData")}</p>}
                  {canRecord && selected && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <Button data-testid={`measure-edit-${c.key}`} variant="ghost" onClick={() => setEditing(c)}
                        className="text-[#FF8A80] hover:bg-[#FF3B30]/10 h-9 px-2">
                        <Pencil className="h-4 w-4 mr-1" /> {c.measurement ? t("measure.edit") : t("measure.record")}
                      </Button>
                      {c.measurement && (
                        <Button data-testid={`measure-delete-${c.key}`} variant="ghost" onClick={() => deleteData(c.measurement.measurement_id)}
                          className="text-red-400 hover:bg-red-500/10 h-9 px-2">
                          <Trash2 className="h-4 w-4 mr-1" /> {t("measure.deleteData")}
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {chartData.length > 0 && (
            <Card data-testid="measure-trend" className="bg-[#10121B] border-white/5">
              <CardContent className="p-5">
                <h2 className="text-base md:text-lg font-semibold text-white inline-flex items-center gap-2 mb-4">
                  <Scale className="h-4 w-4 text-[#10B981]" /> {t("measure.trend")}
                </h2>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="#ffffff12" vertical={false} />
                      <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
                      <YAxis stroke="#64748b" fontSize={11} />
                      <Tooltip contentStyle={{ background: "#161923", border: "1px solid #ffffff1a", borderRadius: 10, color: "#fff" }} />
                      <Line type="monotone" dataKey="weight" name={t("measure.trendWeight")} stroke="#FF3B30" strokeWidth={2.5} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="fat" name={t("measure.trendFat")} stroke="#10B981" strokeWidth={2.5} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {editing && canRecord && selected && (
        <MeasurementForm checkpoint={editing} userId={selected}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadDetail(); loadBatch(); }} />
      )}
    </div>
  );
}

function Stat({ label, value, unit, testId }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500">{label}</div>
      <div data-testid={testId} className="font-[Outfit] font-bold text-white">{value ?? "—"}{value != null ? ` ${unit}` : ""}</div>
    </div>
  );
}

function MeasurementForm({ checkpoint, userId, onClose, onSaved }) {
  const { t } = useLang();
  const m = checkpoint.measurement || {};
  const photoSlot = checkpoint.photo_slot; // "before" | "after" | null
  const [form, setForm] = useState({
    weight: m.weight ?? "", body_fat: m.body_fat ?? "", note: m.note ?? "",
  });
  const [photoBefore, setPhotoBefore] = useState(m.photo_before_file_id || null);
  const [photoAfter, setPhotoAfter] = useState(m.photo_after_file_id || null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const num = (v) => (v === "" || v === null ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/measurements", {
        user_id: userId, checkpoint: checkpoint.key,
        weight: num(form.weight), body_fat: num(form.body_fat),
        photo_before_file_id: photoSlot === "before" ? photoBefore : null,
        photo_after_file_id: photoSlot === "after" ? photoAfter : null,
        note: form.note || null,
      });
      toast.success(t("measure.saved"));
      onSaved();
    } catch (err) { toast.error(extractError(err).message); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="measure-form" className="bg-[#10121B] border-[#FF3B30]/25">
      <CardContent className="p-5">
        <form onSubmit={submit} className="space-y-4">
          <h2 className="text-base md:text-lg font-semibold text-white">
            {checkpoint.label}{checkpoint.is_baseline ? ` · ${t("measure.baseline")}` : checkpoint.is_final ? ` · ${t("measure.final")}` : ""}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("measure.weight")} testId="measure-weight-input" type="number" step="0.1" value={form.weight} onChange={set("weight")} />
            <Field label={t("measure.bodyFat")} testId="measure-fat-input" type="number" step="0.1" value={form.body_fat} onChange={set("body_fat")} />
          </div>
          {photoSlot === "before" && (
            <ImageUpload endpoint="/uploads/measurement-photo" testId="measure-photo-before-input" label={t("measure.photoBefore")} compress="PROGRESS" onUploaded={setPhotoBefore} />
          )}
          {photoSlot === "after" && (
            <ImageUpload endpoint="/uploads/measurement-photo" testId="measure-photo-after-input" label={t("measure.photoAfter")} compress="PROGRESS" onUploaded={setPhotoAfter} />
          )}
          {!photoSlot && (
            <p data-testid="measure-photo-none" className="text-xs text-slate-500">{t("measure.photoNumbersOnly")}</p>
          )}
          <Field label={t("measure.note")} testId="measure-note-input" value={form.note} onChange={set("note")} />
          <div className="flex gap-2 justify-end">
            <Button data-testid="measure-cancel-btn" type="button" variant="ghost" onClick={onClose} className="text-slate-400 hover:bg-white/5">{t("common.cancel")}</Button>
            <Button data-testid="measure-save-btn" type="submit" disabled={busy} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">
              {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {t("common.loading")}</> : t("common.save")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
